'use client';

// React binding for the connection-health monitor (BL-1090).
//
// Thin on purpose: every decision lives in `createConnectionHealthMonitor`, a
// plain controller that's unit-testable in node. This file only owns the
// lifecycle (mount → start, unmount → dispose) and the `useSyncExternalStore`
// bridge — the same split as the realtime LotStateStore / RealtimeLotsProvider
// pairing next door.
//
// Mounted once per app by `RealtimeStack`, so both admin-web and bidder-web
// get it from the provider stack they already render in their root layouts.

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import {
    createConnectionHealthMonitor,
    type ConnectionEnvironment,
    type ConnectionHealthMonitor,
    type ConnectionHealthState,
    type MonitorTimers,
} from '../../isomorphic/connection-health/connection-health-monitor';
import {
    REACHABILITY_PROBE_PATH,
    runReachabilityProbe,
} from '../../isomorphic/connection-health/reachability-probe';

/**
 * Public, unauthenticated, DB-free in both apps — the default probe target.
 * Network-only: see {@link REACHABILITY_PROBE_PATH}.
 */
export const DEFAULT_PROBE_URL = REACHABILITY_PROBE_PATH;

/**
 * Healthy default for consumers rendered outside a provider — shared
 * components on non-tenant routes, and isolated component tests. Returning a
 * module-level constant (rather than a fresh object) keeps the identity stable
 * so a consumer's `useMemo` deps don't churn.
 */
const HEALTHY: ConnectionHealthState = {
    isOffline: false,
    isFeedDisconnected: false,
};

const ConnectionHealthContext = createContext<ConnectionHealthMonitor | null>(
    null,
);

export interface ConnectionHealthProviderProps {
    /** Transport whose connection events drive feed health. */
    subscriber: RealtimeSubscriber;
    /** Probe endpoint. Defaults to {@link DEFAULT_PROBE_URL}. */
    probeUrl?: string;
    /** Test seam — inject a fetch. Production omits it (uses global `fetch`). */
    fetchImpl?: typeof fetch;
    /** Test seam — inject the browser surface. Production omits it. */
    environment?: ConnectionEnvironment;
    /** Test seam — inject timers. Production omits it. */
    timers?: MonitorTimers;
    children: ReactNode;
}

/**
 * Provide app-wide connection health to everything underneath.
 *
 * @param props - {@link ConnectionHealthProviderProps}
 * @returns The provider element.
 *
 * @sideEffect On mount, registers browser online/offline + transport listeners
 *   and may issue reachability probes. Disposed on unmount.
 */
export function ConnectionHealthProvider({
    subscriber,
    probeUrl = DEFAULT_PROBE_URL,
    fetchImpl,
    environment,
    timers,
    children,
}: ConnectionHealthProviderProps) {
    const monitor = useMemo(
        () =>
            createConnectionHealthMonitor({
                subscriber,
                probe: () =>
                    runReachabilityProbe({ url: probeUrl, fetchImpl }),
                environment,
                timers,
            }),
        [subscriber, probeUrl, fetchImpl, environment, timers],
    );

    useEffect(() => monitor.start(), [monitor]);

    return (
        <ConnectionHealthContext.Provider value={monitor}>
            {children}
        </ConnectionHealthContext.Provider>
    );
}

/** Stable no-op for a consumer rendered outside a provider. */
const NO_RECHECK = (): void => {};

/**
 * Ask the app-wide monitor for one reachability probe now (BL-2342). See
 * `ConnectionHealthMonitor.recheck` for when the admin phone app calls it.
 *
 * @returns A stable callback. Outside a provider it is a no-op, matching
 *   {@link useConnectionHealth}'s report-healthy fallback.
 */
export function useRecheckConnection(): () => void {
    const monitor = useContext(ConnectionHealthContext);
    return monitor ? monitor.recheck : NO_RECHECK;
}

/**
 * Read app-wide connection health.
 *
 * @returns `{ isOffline, isFeedDisconnected }`. Outside a provider it reports
 *   healthy rather than throwing, so a shared component on a route that never
 *   mounts the realtime stack still renders.
 */
export function useConnectionHealth(): ConnectionHealthState {
    const monitor = useContext(ConnectionHealthContext);

    // Hooks run unconditionally (rules of hooks); the no-provider case is
    // handled by pointing the store at constants rather than by early-return.
    return useSyncExternalStore(
        useMemo(
            () => (monitor ? monitor.subscribe : () => () => {}),
            [monitor],
        ),
        useMemo(
            () => (monitor ? monitor.getState : () => HEALTHY),
            [monitor],
        ),
        // Server snapshot: the monitor is inert during SSR, so first paint is
        // always healthy and the client settles it after hydration.
        () => HEALTHY,
    );
}
